import { useCallback, useRef, useState } from 'react';
import { FormSnapshot } from './types';

const arraysEqualUnordered = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
};

type ClinicPart = Pick<
  FormSnapshot,
  | 'operatorPhone'
  | 'sipAccountId'
  | 'callPoolId'
  | 'schedule'
  | 'holidays'
  | 'callSchedule'
  | 'callHolidays'
>;

type SlotPart = Pick<
  FormSnapshot,
  | 'callsMaxTries'
  | 'callsIntervalMinutes'
  | 'callsDisable'
  | 'excludedSpecializationIds'
  | 'excludedDoctorIds'
>;

export const useDirtyTracking = () => {
  const initialSnapshot = useRef<FormSnapshot | null>(null);
  const [snapshotReady, setSnapshotReady] = useState(false);

  const clinicRef = useRef<ClinicPart | null>(null);
  const slotRef = useRef<SlotPart | null>(null);

  const tryAssemble = useCallback(() => {
    if (!clinicRef.current || !slotRef.current || initialSnapshot.current)
      return;

    initialSnapshot.current = {
      ...clinicRef.current,
      ...slotRef.current,
    };
    setSnapshotReady(true);
  }, []);

  const setClinicSnapshot = useCallback(
    (data: ClinicPart) => {
      clinicRef.current = data;
      tryAssemble();
    },
    [tryAssemble],
  );

  const setSlotSnapshot = useCallback(
    (data: SlotPart) => {
      slotRef.current = data;
      tryAssemble();
    },
    [tryAssemble],
  );

  const updateSnapshot = useCallback((current: FormSnapshot) => {
    initialSnapshot.current = { ...current };
  }, []);

  /** Обновляет только callSchedule/callHolidays в снимке (после загрузки с API) */
  const updateCallScheduleSnapshot = useCallback(
    (callSchedule: FormSnapshot['callSchedule'], callHolidays: string[]) => {
      if (!initialSnapshot.current) {
        // Снимок ещё не собран — обновляем clinicRef, чтобы tryAssemble подхватил
        if (clinicRef.current) {
          clinicRef.current = {
            ...clinicRef.current,
            callSchedule: callSchedule.map((d) => ({ ...d })),
            callHolidays: [...callHolidays],
          };
        }
        return;
      }
      initialSnapshot.current = {
        ...initialSnapshot.current,
        callSchedule: callSchedule.map((d) => ({ ...d })),
        callHolidays: [...callHolidays],
      };
    },
    [],
  );

  /** Изменились ли базовые параметры клиники (телефон, SIP, пул обзвона) */
  const computeIsClinicBasicDirty = useCallback(
    (current: FormSnapshot): boolean => {
      if (!snapshotReady || !initialSnapshot.current) return false;
      const snap = initialSnapshot.current;

      if (current.operatorPhone !== snap.operatorPhone) return true;
      if (current.sipAccountId !== snap.sipAccountId) return true;
      return current.callPoolId !== snap.callPoolId;
    },
    [snapshotReady],
  );

  /** Изменилось ли расписание / праздники */
  const computeIsScheduleDirty = useCallback(
    (current: FormSnapshot): boolean => {
      if (!snapshotReady || !initialSnapshot.current) return false;
      const snap = initialSnapshot.current;

      const holidaysChanged =
        current.holidays.length !== snap.holidays.length ||
        current.holidays.some((h, i) => h !== snap.holidays[i]);
      if (holidaysChanged) return true;

      return current.schedule.some((d, i) => {
        const orig = snap.schedule[i];
        return d.start !== orig.start || d.end !== orig.end;
      });
    },
    [snapshotReady],
  );

  /** Изменилось ли расписание обзвона / праздники обзвона */
  const computeIsCallScheduleDirty = useCallback(
    (current: FormSnapshot): boolean => {
      if (!snapshotReady || !initialSnapshot.current) return false;
      const snap = initialSnapshot.current;

      const holidaysChanged =
        current.callHolidays.length !== snap.callHolidays.length ||
        current.callHolidays.some((h, i) => h !== snap.callHolidays[i]);
      if (holidaysChanged) return true;

      return current.callSchedule.some((d, i) => {
        const orig = snap.callSchedule[i];
        return d.start !== orig.start || d.end !== orig.end;
      });
    },
    [snapshotReady],
  );

  const computeIsSlotDirty = useCallback(
    (current: FormSnapshot): boolean => {
      if (!snapshotReady || !initialSnapshot.current) return false;
      const snap = initialSnapshot.current;

      if (
        !arraysEqualUnordered(
          current.excludedSpecializationIds,
          snap.excludedSpecializationIds,
        )
      )
        return true;

      return !arraysEqualUnordered(
        current.excludedDoctorIds,
        snap.excludedDoctorIds,
      );
    },
    [snapshotReady],
  );

  const computeIsDirty = useCallback(
    (current: FormSnapshot): boolean =>
      computeIsClinicBasicDirty(current) ||
      computeIsScheduleDirty(current) ||
      computeIsSlotDirty(current),
    [computeIsClinicBasicDirty, computeIsScheduleDirty, computeIsSlotDirty],
  );

  return {
    snapshotReady,
    setClinicSnapshot,
    setSlotSnapshot,
    updateSnapshot,
    updateCallScheduleSnapshot,
    computeIsDirty,
    computeIsClinicBasicDirty,
    computeIsScheduleDirty,
    computeIsCallScheduleDirty,
    computeIsSlotDirty,
  };
};
